import React from 'react';

/**
 * CodeFlicker 订阅服务协议 - 英文版
 */
const EN: React.FC = () => {
  return (
    <div className="mx-auto max-w-4xl px-6 py-12">
      <h1 className="mb-8 text-4xl font-bold text-gray-900">
        CodeFlicker Subscription Services Agreement
      </h1>

      <div className="mb-6 text-sm text-gray-600">
        <p>
          <strong>Release Date:</strong> 2026/3/20
        </p>
        <p>
          <strong>Effective Date:</strong> 2026/3/20
        </p>
      </div>

      <div className="space-y-8 text-gray-700">
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">1. Scope of Agreement</h2>
          <p className="leading-relaxed">
            This Agreement applies to all subscription-based services that you purchase and use
            through CodeFlicker.
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            2. Subscription Content and Service
          </h2>
          <ul className="list-disc space-y-2 pl-6 leading-relaxed">
            <li>
              The content, features, rules of use, and term of the subscription service are subject
              to what is displayed on the subscription page at the time of subscription.
            </li>
            <li>
              CodeFlicker reserves the right to adjust the content and form of the subscription
              service based on business needs, and will notify you in advance within a reasonable
              scope.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">3. Fees and Payment</h2>
          <ul className="list-disc space-y-2 pl-6 leading-relaxed">
            <li>
              The subscription service is a paid service. The fee standard is subject to what is
              displayed on the subscription page or as otherwise notified by CodeFlicker.
            </li>
            <li>
              You must provide accurate and valid payment information and authorize CodeFlicker or
              its partners (if any) to automatically charge your payment method for fees at each
              renewal.
            </li>
            <li>
              Unless otherwise required by mandatory law or specified by CodeFlicker, all fees paid
              are non-refundable.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            4. Auto-Renewal and Cancellation
          </h2>
          <ul className="list-disc space-y-2 pl-6 leading-relaxed">
            <li>
              The subscription service enables the auto-renewal mechanism by default, meaning it
              will automatically renew and charge fees at the end of each subscription period until
              you actively cancel it.
            </li>
            <li>
              If your subscription includes a minimum service period (the &quot;Initial
              Period&quot;), the subscription will auto-renew for successive renewal periods of
              equal length after the Initial Period expires.
            </li>
            <li>
              You may cancel auto-renewal through the means provided by CodeFlicker at least 2 days
              before the end of your current subscription period. After cancellation, you may
              continue to use the paid subscription service until the current period ends.
            </li>
            <li>Refer to Automatic Renewal Service Rules for relevant automatic renewal rules.</li>
          </ul>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">5. Fee Adjustments</h2>
          <p className="leading-relaxed">
            CodeFlicker reserves the right to adjust subscription fees and will notify you before
            the new price takes effect. If you do not agree to the adjustment, you may cancel your
            subscription before the renewal. If you continue using the service after renewal, you
            will be deemed to have accepted the new subscription price.
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">6. Usage Norms</h2>

          <h3 className="mb-3 mt-4 text-xl font-semibold text-gray-800">Basic Norms</h3>
          <ul className="list-disc space-y-2 pl-6 leading-relaxed">
            <li>
              You shall comply with applicable laws, regulations, policies, and industry standards
              during use, and label content generated/synthesized by AI during dissemination and
              use. You shall not use the subscription service for any illegal or non-compliant
              purposes, nor for any activities that undermine public order, good customs, or the
              rights of others.
            </li>
            <li>
              You shall abide by the usage rules, User Agreement, Service Agreements, and other
              relevant agreements of the CodeFlicker platform.
            </li>
            <li>
              Violation of this Agreement may result in suspension or termination of service, and
              any fees paid will not be refunded.
            </li>
          </ul>

          <h3 className="mb-3 mt-4 text-xl font-semibold text-gray-800">
            Usage Scenario Restrictions
          </h3>
          <ul className="list-disc space-y-2 pl-6 leading-relaxed">
            <li>
              You understand and agree that the CodeFlicker is a code assistance subscription
              service for developers, and its usage quota is limited to use within CodeFlicker
              platform.
            </li>
            <li>
              You shall not use the CodeFlicker's usage quota for general API access or other
              scenarios outside the CodeFlicker platform, including but not limited to directly
              calling relevant model interfaces in self-built applications, bots, websites, SaaS
              products, or other systems, unless you have separately entered into a written
              agreement with CodeFlicker.
            </li>
            <li>
              Unless otherwise agreed in writing, you shall not provide the CodeFlicker to any third
              party through means such as resale, agency sale, repackaging, or aggregated
              forwarding, nor shall you use the CodeFlicker platform to provide paid or free model
              capability services to third parties.
            </li>
          </ul>

          <h3 className="mb-3 mt-4 text-xl font-semibold text-gray-800">
            Personal Use Restrictions
          </h3>
          <ul className="list-disc space-y-2 pl-6 leading-relaxed">
            <li>
              CodeFlicker subscription is based on a single account and is authorized for personal
              use only by the natural person bound to that account. It cannot be shared with
              sub-accounts.
            </li>
            <li>
              You shall not allow others (including but not limited to your colleagues, friends,
              clients, or any organization) to actually use your CodeFlicker usage quota by means of
              account sharing, renting, lending, transferring, sublicensing, or any other method.
            </li>
            <li>
              If CodeFlicker reasonably suspects that you engage in activities such as account
              sharing, batch calls, providing services on behalf of others, reselling access rights,
              or other acts that may harm the fair use order of the platform or the legitimate
              rights and interests of third parties, CodeFlicker has the right to take measures
              including but not limited to restricting certain features, reducing or limiting usage
              quotas, suspending or terminating service, reclaiming remaining quotas, etc., and may
              pursue corresponding liabilities according to this Agreement and applicable laws.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">7. Limitation of Liability</h2>
          <p className="leading-relaxed">
            CodeFlicker will make reasonable efforts to ensure the stability and security of the
            subscription service but does not guarantee that the service will be completely free of
            defects or meet your expectations. CodeFlicker shall not be held liable for service
            interruptions caused by force majeure, network failures, or third-party reasons.
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            8. Agreement Modification and Termination
          </h2>
          <ul className="list-disc space-y-2 pl-6 leading-relaxed">
            <li>
              CodeFlicker reserves the right to modify this Agreement as needed, and the modified
              agreement will be published on the platform and take effect from the date of
              publication.
            </li>
            <li>
              If you continue using the subscription service after the agreement is modified, you
              will be deemed to have accepted the modified agreement.
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default EN;
